import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popconfirm, Row, Space } from "antd";
import { ColumnProps } from "antd/lib/table";
import { Button } from "Components/General/Button";
import { SearchBar } from "Components/General/SearchBar";
import i18n, { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { projectGroupList } from "Pages/Admin/Stores/ProjectGroupStore";
import { systemRolesStore } from "Pages/Admin/Stores/SystemRolesStore";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { stringSort } from "utils/defaultSort";
import { SystemRolesOverviewTableModal } from "./SystemRolesOverviewTableModal";
import { Table } from "Components/General/Table";

interface SystemRolesOverviewProps {
  prGroupId?: string;
}

export const SystemRolesOverviewTable = observer(({ prGroupId }: SystemRolesOverviewProps) => {
  const { t } = useTranslation(I18N_NS.ADMIN);

  useEffect(() => {
    (async () => {
      if (prGroupId && !projectGroupList.data.length) {
        await projectGroupList.fetch();
      }
      systemRolesStore.fetch();
    })();
  }, []);

  const handleSearch = async (parameter: string) => {
    systemRolesStore.searchSystemRole(parameter);
  };

  const handleDelete = (record: API_Systemrecht) => {
    systemRolesStore.deleteSystemRole(record.userId);
  };

  const linkCell = (text: string, record: API_User) => {
    const url = prGroupId ? `/admin/pg/${prGroupId}/user/edit/${record.id}` : `/admin/user/edit/${record.id}`;
    return <Link to={url}>{text}</Link>;
  };

  const columns: ColumnProps<API_Systemrecht>[] = [
    {
      dataIndex: "id",
      title: t("User ID"),
      render: linkCell,
      sorter: (a, b) => a.id - b.id,
    },
    {
      dataIndex: "userId",
      title: t("Login name"),
      render: linkCell,
      sorter: (a, b) => stringSort(a, b, "userId"),
    },
    {
      dataIndex: "firstName",
      title: t("Firstname"),
      sorter: (a, b) => stringSort(a, b, "firstName"),
    },
    {
      dataIndex: "surname",
      title: t("Surname"),
      sorter: (a, b) => stringSort(a, b, "surname"),
    },
    {
      dataIndex: "email",
      title: t("Email"),
      sorter: (a, b) => stringSort(a, b, "email"),
    },
    {
      dataIndex: "recht",
      title: t("Role"),
      sorter: (a, b) => stringSort(a, b, "recht"),
      width: 110,
      filters: [
        {
          text: "ADMIN",
          value: "ADMIN",
        },
        {
          text: "SUP",
          value: "SUP",
        },
        {
          text: "VERWALTUNG",
          value: "VERWALTUNG",
        },
        {
          text: "TECH",
          value: "TECH",
        },
        {
          text: "STATUS",
          value: "STATUS",
        },
      ],
      onFilter: (value: string, record) => record.recht.indexOf(value) === 0,
    },
    {
      align: "center",
      dataIndex: "actions",
      render: (_, record) => (
        <Popconfirm title={t("Are you sure?")} onConfirm={() => handleDelete(record)} okText={t("Yes")}>
          <FontAwesomeIcon icon={"trash-alt"} size={"lg"} style={{ cursor: "pointer" }} />
        </Popconfirm>
      ),
    },
  ];
  return (
    <>
      <Row justify="space-between" align="middle">
        <h1>{t("System roles")}</h1>
      </Row>
      <Row justify="space-between" style={{ flexWrap: "nowrap" }}>
        <SearchBar
          placeholder={i18n.t("Search within user id, name, email and contact data", { ns: I18N_NS.ADMIN })}
          onSearch={handleSearch}
          style={{ marginRight: 10, marginBottom: 0 }}
        />
        <Space>
          <Button faIcon={"plus"} type={"primary"} onClick={systemRolesStore.toggleSystemRolesModal}>
            {t("Assign new role")}
          </Button>
        </Space>
      </Row>
      <Table<API_Systemrecht>
        name="adminSysOverview"
        dataSource={systemRolesStore.data}
        columns={columns}
        rowKey="id"
        loading={systemRolesStore.state === "LOADING"}
        rowClassName={(record) => (record.active ? "" : "disabledCell")}
      />
      <SystemRolesOverviewTableModal
        isVisible={systemRolesStore.isSystemRolesModalVisible}
        toggleModal={systemRolesStore.toggleSystemRolesModal}
        onRoleUpdate={systemRolesStore.updateSystemRole}
      />
    </>
  );
});
